import type { Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { db, getMeta, setMeta, wipeLocalData } from '../db/db';
import { currentUserId, refreshSession } from '../lib/auth';
import { HOUSEHOLD_NOTICE_KEY, adoptHousehold, clearExpectedHousehold, expectHouseholdChange, syncEngine } from '../sync/engine';

export interface Member {
  userId: Id;
  name: string;
  email: string;
  image: string | null;
  role: string;
}

/** Zaproszenie wysłane z mojego domu, czekające na odpowiedź. */
export interface PendingInvite {
  id: Id;
  email: string;
  createdMs: number;
  expiresMs: number;
  invitedByName: string;
}

/** Zaproszenie do cudzego domu, wystawione na mój e-mail. */
export interface MyInvite {
  id: Id;
  householdName: string;
  invitedByName: string;
  expiresMs: number;
}

export interface InviteByToken extends MyInvite {
  email: string;
}

export interface HouseholdInfo {
  household: { id: Id; name: string };
  members: Member[];
  invites: PendingInvite[];
  /** Kiedy pobrano z serwera. */
  fetchedMs: number;
}

/** Błąd API domu z komunikatem do pokazania; `code` = pole error z serwera (np. 'expired'). */
export class HouseholdError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
  }
}

// Kody z pola error odpowiedzi /api/household/* (not_found i bad_request — komunikat zależy od akcji).
const MESSAGES: Record<string, string> = {
  offline: 'Brak połączenia z internetem. Spróbuj ponownie, gdy będziesz online.',
  unauthorized: 'Sesja wygasła — zaloguj się ponownie.',
  bad_email: 'To nie wygląda na poprawny adres e-mail.',
  bad_name: 'Nazwa domu musi mieć od 1 do 60 znaków.',
  already_member: 'Ta osoba już należy do Twojego domu.',
  mail_failed: 'Nie udało się wysłać e-maila z zaproszeniem. Spróbuj ponownie.',
  last_member: 'Jesteś jedyną osobą w tym domu, więc nie możesz go opuścić.',
  wrong_email: 'To zaproszenie wysłano na inny adres e-mail niż ten, którym się zalogowano.',
  other_members: 'Nie można dołączyć: w Twoim obecnym domu są jeszcze inne osoby. Najpierw z niego wyjdź.',
  already_used: 'To zaproszenie zostało już wykorzystane.',
  used: 'To zaproszenie zostało już przyjęte albo odrzucone.',
  expired: 'Zaproszenie wygasło. Poproś o nowe.',
  household_changed: 'Dom właśnie się zmienia. Spróbuj ponownie za chwilę.',
};
/** 409 household_changed (wyścig ze scaleniem domów): jedna ponowna próba po chwili. */
const HOUSEHOLD_RETRY_MS = 1000;

const INVITE_NOT_FOUND = 'To zaproszenie nie istnieje albo zostało anulowane.';

async function api<T>(method: string, path: string, body: unknown, fallback: string, notFound = fallback): Promise<T> {
  try {
    return await request<T>(method, path, body, fallback, notFound);
  } catch (e) {
    if (!(e instanceof HouseholdError && e.code === 'household_changed')) throw e;
    await new Promise((r) => setTimeout(r, HOUSEHOLD_RETRY_MS));
    return request<T>(method, path, body, fallback, notFound);
  }
}

async function request<T>(method: string, path: string, body: unknown, fallback: string, notFound: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });
  } catch {
    throw new HouseholdError(MESSAGES.offline!, 'offline', 0);
  }
  const data: unknown = res.headers.get('content-type')?.includes('json') ? await res.json().catch(() => null) : null;
  if (res.ok) return data as T;
  const code = (data as { error?: string } | null)?.error ?? `http_${res.status}`;
  const byStatus: Record<number, string | undefined> = { 401: MESSAGES.unauthorized, 404: notFound, 410: MESSAGES.expired };
  throw new HouseholdError(MESSAGES[code] ?? byStatus[res.status] ?? fallback, code, res.status);
}

// --- Dom i domownicy (kopia w meta, żeby imiona były dostępne offline) ---

const CACHE_KEY = 'household';

export function useHousehold(): HouseholdInfo | null | undefined {
  return useLiveQuery(async () => (await getMeta<HouseholdInfo>(CACHE_KEY)) ?? null);
}

let refreshing: Promise<void> | null = null;
let refreshAgain = false;
let lastRefreshMs = 0;

/**
 * Pobiera dom, domowników i zaproszenia do cache. Nie rzuca (offline zostaje poprzednia kopia).
 * ifOlderThanMs: pomija, jeśli ostatnia próba była niedawno (np. po każdej synchronizacji).
 */
export function refreshHousehold(opts: { ifOlderThanMs?: number } = {}): Promise<void> {
  if (opts.ifOlderThanMs !== undefined && Date.now() - lastRefreshMs < opts.ifOlderThanMs) return refreshing ?? Promise.resolve();
  if (refreshing) {
    // Zmiana w trakcie pobierania (np. po zaproszeniu) — pobieramy jeszcze raz po bieżącym.
    refreshAgain = true;
    return refreshing;
  }
  refreshing = (async () => {
    do {
      refreshAgain = false;
      lastRefreshMs = Date.now();
      try {
        const data = await api<Omit<HouseholdInfo, 'fetchedMs'>>('GET', '/api/household', undefined, 'Nie udało się wczytać domu.');
        await setMeta(CACHE_KEY, { ...data, fetchedMs: Date.now() } satisfies HouseholdInfo);
      } catch {
        // offline albo chwilowy błąd serwera
      }
    } while (refreshAgain);
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

const NO_MEMBERS = new Map<string, Member>();

/** Domownicy po userId (pusta mapa, dopóki nie ma kopii). */
export function useMembers(): Map<string, Member> {
  const info = useHousehold();
  return useMemo(() => (info ? new Map(info.members.map((m) => [m.userId, m])) : NO_MEMBERS), [info]);
}

/** Imię do krótkich komunikatów („Magda w Auchan”); bez danych „Ktoś”. */
export function memberFirstName(m?: Member): string {
  const first = m?.name?.trim().split(/\s+/)[0];
  if (first) return first;
  const local = m?.email?.split('@')[0]?.split(/[._+-]/)[0];
  if (local) return local.charAt(0).toLocaleUpperCase('pl-PL') + local.slice(1);
  return 'Ktoś';
}

export function memberInitial(m?: Member): string {
  return m ? memberFirstName(m).charAt(0).toLocaleUpperCase('pl-PL') : '?';
}

export async function renameHousehold(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length > 60) throw new HouseholdError(MESSAGES.bad_name!, 'bad_name', 0);
  await api('PATCH', '/api/household', { name: trimmed }, 'Nie udało się zmienić nazwy domu.');
  await refreshHousehold();
}

export async function inviteMember(email: string): Promise<PendingInvite> {
  const value = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) throw new HouseholdError(MESSAGES.bad_email!, 'bad_email', 0);
  const { invite } = await api<{ invite: PendingInvite }>('POST', '/api/household/invites', { email: value }, 'Nie udało się wysłać zaproszenia.');
  await refreshHousehold();
  return invite;
}

export async function cancelInvite(id: Id): Promise<void> {
  try {
    await api('DELETE', `/api/household/invites/${encodeURIComponent(id)}`, undefined, 'Nie udało się anulować zaproszenia.');
  } catch (e) {
    // Już nie istnieje (przyjęte albo anulowane na innym urządzeniu) — skutek ten sam.
    if (!(e instanceof HouseholdError && e.status === 404)) throw e;
  }
  await refreshHousehold();
}

/** Usuwa domownika; własne id = opuszczenie domu (leaveHousehold). */
export async function removeMember(userId: Id): Promise<void> {
  if (userId === currentUserId()) return leaveHousehold();
  try {
    await api('DELETE', `/api/household/members/${encodeURIComponent(userId)}`, undefined, 'Nie udało się usunąć domownika.');
  } catch (e) {
    // Już usunięty (np. sam wyszedł) — skutek ten sam.
    if (!(e instanceof HouseholdError && e.status === 404)) throw e;
  }
  await refreshHousehold();
}

/**
 * Opuszcza wspólny dom. Listy i sklepy zostają u pozostałych domowników, a to urządzenie
 * zaczyna od nowego, pustego domu — dlatego najpierw wysyłamy niezsynchronizowane zmiany.
 */
export async function leaveHousehold(): Promise<void> {
  const me = currentUserId();
  if (!me) throw new HouseholdError(MESSAGES.unauthorized!, 'unauthorized', 401);
  await syncEngine.run();
  if ((await db.outbox.count()) > 0) {
    throw new HouseholdError('Najpierw połącz się z internetem, żeby wysłać ostatnie zmiany.', 'offline', 0);
  }
  await api('DELETE', `/api/household/members/${encodeURIComponent(me)}`, undefined, 'Nie udało się opuścić domu.');
  await wipeLocalData(['userId']);
  await refreshSession();
  await syncEngine.run();
  await refreshHousehold();
}

// --- Zaproszenia do mnie ---

let myInvites: MyInvite[] | undefined;
let invitesLoading: Promise<void> | null = null;
const inviteListeners = new Set<() => void>();

function setMyInvites(next: MyInvite[]) {
  myInvites = next;
  for (const fn of inviteListeners) fn();
}

export function refreshMyInvites(): Promise<void> {
  invitesLoading ??= api<{ invites: MyInvite[] }>('GET', '/api/household/invites/mine', undefined, 'Nie udało się wczytać zaproszeń.')
    .then(({ invites }) => setMyInvites(invites))
    .catch(() => {
      // offline: zostaje poprzednia lista (albo pusta przy pierwszym wczytaniu)
      if (myInvites === undefined) setMyInvites([]);
    })
    .finally(() => {
      invitesLoading = null;
    });
  return invitesLoading;
}

/** Zaproszenia do innych domów wystawione na mój e-mail (undefined = wczytywanie). */
export function useMyInvites(): MyInvite[] | undefined {
  const value = useSyncExternalStore(
    (fn) => {
      inviteListeners.add(fn);
      return () => void inviteListeners.delete(fn);
    },
    () => myInvites,
  );
  useEffect(() => {
    const refetch = () => void refreshMyInvites();
    refetch();
    window.addEventListener('online', refetch);
    return () => window.removeEventListener('online', refetch);
  }, []);
  return value;
}

/** Zaproszenie z linku /join?token=… (do ekranu zgody). */
export async function getInviteByToken(token: string): Promise<InviteByToken> {
  const { invite } = await api<{ invite: InviteByToken }>(
    'GET',
    `/api/household/invites/by-token/${encodeURIComponent(token)}`,
    undefined,
    'Nie udało się wczytać zaproszenia.',
    INVITE_NOT_FOUND,
  );
  return invite;
}

/**
 * Dołącza do domu z zaproszenia. Dane lokalne zostają — serwer przeniósł je do wspólnego domu,
 * a silnik synchronizacji pobiera całość od zera (niewysłane zmiany trafią tam przez sesję).
 * Zapowiedź zmiany domu odróżnia to od usunięcia z domu, po którym dane lokalne są kasowane.
 */
export async function joinHousehold(input: { inviteId?: Id; token?: string }): Promise<Id> {
  const body = input.inviteId ? { inviteId: input.inviteId } : { token: input.token };
  await expectHouseholdChange(null);
  let householdId: Id;
  try {
    ({ householdId } = await api<{ householdId: Id }>('POST', '/api/household/join', body, 'Nie udało się dołączyć do domu.', INVITE_NOT_FOUND));
    await expectHouseholdChange(householdId);
    await refreshSession();
    await adoptHousehold(householdId);
  } finally {
    await clearExpectedHousehold();
  }
  await syncEngine.run();
  await refreshHousehold();
  void refreshMyInvites();
  return householdId;
}

export async function declineInvite(id: Id): Promise<void> {
  await api('POST', `/api/household/invites/${encodeURIComponent(id)}/decline`, undefined, 'Nie udało się odrzucić zaproszenia.', INVITE_NOT_FOUND);
  if (myInvites) setMyInvites(myInvites.filter((i) => i.id !== id));
  void refreshMyInvites();
}

/** Komunikat po usunięciu z domu przez domownika ('removed'); null = brak. */
export function useHouseholdNotice(): 'removed' | null {
  return (useLiveQuery(async () => ((await getMeta<string>(HOUSEHOLD_NOTICE_KEY)) === 'removed' ? 'removed' : null), []) ?? null) as
    | 'removed'
    | null;
}

export async function dismissHouseholdNotice(): Promise<void> {
  await db.meta.delete(HOUSEHOLD_NOTICE_KEY);
}
